import React, { useState } from "react";
import { PROFILES, PHASES, TEMPLATES } from "@/compass/data";
import { loadDrafts } from "./draftStore";
import {
  completePhase,
  currentPhaseNumber,
  effectiveCompleted,
  phaseReadiness,
  reopenPhase,
  templateHasRequiredInformation,
} from "./progressRules";

function StatusBadge({ status }) {
  const map = {
    done: ["DONE", "bg-emerald-100 text-emerald-800"],
    current: ["CURRENT", "bg-emerald-800 text-white"],
    later: ["PREVIEW", "bg-stone-100 text-stone-500"],
    review: ["PRIOR-WORK REVIEW", "bg-amber-100 text-amber-800"],
    reconfirm: ["RECONFIRM", "bg-amber-100 text-amber-800"],
  };
  const [label, cls] = map[status] || map.later;
  return <span className={`text-[10px] font-bold tracking-wider px-2 py-1 rounded-full ${cls}`}>{label}</span>;
}

const emptyRecord = {
  evidenceDetails: "",
  evidenceReviewer: "",
  noEvidenceReviewer: false,
  questionsDetails: "",
  questionsOwner: "",
  noUnresolvedQuestions: false,
};

export function Roadmap({ openTemplateModal, openResources, progress, setProgress, saveStatus }) {
  const [isEditingSetup, setIsEditingSetup] = useState(false);
  const [openPhaseN, setOpenPhaseN] = useState(null);
  const [reopenConfirm, setReopenConfirm] = useState(null);
  const [setupDraft, setSetupDraft] = useState({
    productName: progress.productName || "",
    profileId: progress.profileId || "dry-seasoning",
    startingPhase: progress.startingPhase || 1,
  });

  const isSetup = Boolean(progress.productName?.trim());
  const currentProfile = PROFILES.find((p) => p.id === progress.profileId) || PROFILES[0];

  if (!isSetup || isEditingSetup) {
    return (
      <div className="max-w-2xl mx-auto rounded-xl bg-white border border-stone-200 p-6 sm:p-8">
        <h2 className="text-2xl font-bold text-stone-900 mb-2">Let's build your guided roadmap</h2>
        <p className="text-sm text-stone-600 mb-6">Compass is the founder workspace. Alaine's Bench is internal-only and is not opened to the public.</p>
        <div className="space-y-5">
          <div>
            <label htmlFor="compass-product-name" className="block text-sm font-semibold text-stone-900 mb-1.5">Project or Product Name</label>
            <input id="compass-product-name" value={setupDraft.productName}
              onChange={(e) => setSetupDraft((p) => ({ ...p, productName: e.target.value }))}
              placeholder="e.g. Grandma's Hot Sauce"
              className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-700/40" />
          </div>
          <div>
            <label htmlFor="compass-product-profile" className="block text-sm font-semibold text-stone-900 mb-1.5">What type of product are you making?</label>
            <select id="compass-product-profile" value={setupDraft.profileId}
              onChange={(e) => setSetupDraft((p) => ({ ...p, profileId: e.target.value }))}
              className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm">
              {PROFILES.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="compass-start-stage" className="block text-sm font-semibold text-stone-900 mb-1.5">Where are you right now?</label>
            <select id="compass-start-stage" value={setupDraft.startingPhase}
              onChange={(e) => setSetupDraft((p) => ({ ...p, startingPhase: Number(e.target.value) }))}
              className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm">
              {PHASES.map((phase) => <option key={phase.n} value={phase.n}>Phase {phase.n}: {phase.name}</option>)}
            </select>
          </div>
          {setupDraft.startingPhase > 1 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
              Starting at phase {setupDraft.startingPhase} does not bypass earlier work. Compass will guide you through a separate, phase-by-phase review of phases 1–{setupDraft.startingPhase - 1}, including evidence and open-question ownership, before phase {setupDraft.startingPhase}.
            </div>
          )}
          <button disabled={!setupDraft.productName.trim()} className="px-6 py-2.5 rounded-full bg-emerald-800 disabled:opacity-50 text-white font-semibold text-sm"
            onClick={() => {
              if (!setupDraft.productName.trim()) return;
              setProgress((p) => ({
                ...p,
                productName: setupDraft.productName.trim(),
                profileId: setupDraft.profileId,
                startingPhase: setupDraft.startingPhase,
              }));
              setOpenPhaseN(1);
              setIsEditingSetup(false);
            }}>
            {isEditingSetup ? "Save setup" : setupDraft.startingPhase > 1 ? "Begin prior-work review" : "Start roadmap"}
          </button>
          {isEditingSetup && <button onClick={() => setIsEditingSetup(false)} className="ml-4 text-sm underline">Cancel</button>}
          <p className="text-xs text-stone-500">Not sure where to start? Choose Concept brief. Prior work is reviewed, never silently marked complete.</p>
        </div>
      </div>
    );
  }

  const done = effectiveCompleted(progress);
  const currentN = currentPhaseNumber(progress);
  const allDone = done.length === PHASES.length;
  const currentPhase = PHASES.find((phase) => phase.n === currentN);
  const visiblePhaseN = openPhaseN ?? currentN;
  const drafts = loadDrafts();

  const updateRecord = (phaseN, change) => setProgress((p) => ({
    ...p,
    phaseRecords: {
      ...(p.phaseRecords || {}),
      [phaseN]: { ...emptyRecord, ...(p.phaseRecords?.[phaseN] || {}), ...change },
    },
  }));

  const toggleChecklist = (phaseN, index) => setProgress((p) => {
    const existing = p.checklists?.[phaseN] || [];
    return {
      ...p,
      checklists: {
        ...p.checklists,
        [phaseN]: existing.includes(index) ? existing.filter((i) => i !== index) : [...existing, index],
      },
    };
  });

  return (
    <div className="grid lg:grid-cols-[1fr,300px] gap-8 items-start">
      <div className="space-y-6">
        <div className="flex items-center justify-between rounded-xl bg-white border border-stone-200 p-6">
          <div>
            <div className="text-xs font-bold tracking-widest text-emerald-800 uppercase mb-1">{currentProfile.name}</div>
            <h1 className="text-2xl sm:text-3xl font-bold text-stone-900">{progress.productName}</h1>
          </div>
          <button onClick={() => {
            setSetupDraft({ productName: progress.productName, profileId: progress.profileId, startingPhase: progress.startingPhase });
            setIsEditingSetup(true);
          }} className="text-sm font-semibold text-stone-500 hover:text-stone-900">Edit setup</button>
        </div>

        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5">
          <p className="font-semibold text-emerald-950">{allDone ? "Your roadmap record is complete" : `Your next step: ${currentPhase.name}`}</p>
          <p className="mt-2 text-sm text-stone-700">{allDone ? "Review your records with the appropriate technical professionals before launch." : currentN < progress.startingPhase ? "Review and document this prior work before moving to your selected starting phase." : currentPhase.preparation}</p>
          <p role="status" className="mt-3 text-xs text-stone-600">{saveStatus}</p>
          <p className="mt-2 text-xs text-stone-600">{done.length} of {PHASES.length} phases confirmed. Completion is your progress record—not regulatory approval or a food-safety assessment.</p>
        </div>

        <div className="space-y-4">
          {PHASES.map((phase) => {
            const isCompleted = done.includes(phase.n);
            const needsReconfirmation = (progress.readinessReconfirmationRequired || []).includes(phase.n);
            const isCurrent = phase.n === currentN && !allDone;
            const isOpen = visiblePhaseN === phase.n;
            const canEdit = isCurrent;
            const record = { ...emptyRecord, ...(progress.phaseRecords?.[phase.n] || {}) };
            const readiness = phaseReadiness(progress, phase, drafts);
            const status = needsReconfirmation ? "reconfirm" : isCompleted ? "done" : isCurrent ? (phase.n < progress.startingPhase ? "review" : "current") : "later";

            return (
              <div key={phase.n} className={`rounded-xl border ${isOpen ? "bg-white border-emerald-300 shadow-sm" : "bg-stone-50 border-stone-200"}`}>
                <div className="p-6">
                  <button onClick={() => setOpenPhaseN(isOpen ? 0 : phase.n)} aria-expanded={isOpen}
                    className="w-full flex items-start justify-between gap-4 text-left">
                    <div className="flex gap-4">
                      <div className={`mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-bold text-sm ${isCompleted ? "bg-emerald-100 text-emerald-800" : isCurrent ? "bg-emerald-800 text-white" : "bg-stone-200 text-stone-500"}`}>{isCompleted ? "✓" : phase.n}</div>
                      <div><h3 className="font-bold text-lg text-stone-900">{phase.name}</h3><p className="mt-1 text-sm text-stone-600">{phase.desc}</p></div>
                    </div>
                    <div className="flex items-center gap-3"><StatusBadge status={status} /><span className="text-stone-400">▼</span></div>
                  </button>

                  {isOpen && (
                    <div className="mt-6 sm:ml-12 space-y-6">
                      {!canEdit && !isCompleted && <div className="rounded-lg bg-stone-100 p-3 text-sm text-stone-600">Preview only. Complete the current phase first to unlock this phase.</div>}
                      {needsReconfirmation && <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">Earlier work changed or completion needs review. Your saved work is retained; reconfirm readiness before advancing.</div>}
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div><div className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-2">What & Why</div><p className="text-sm text-stone-700"><b>What:</b> {phase.what}</p><p className="mt-2 text-sm text-stone-700"><b>Why:</b> {phase.why}</p></div>
                        <div><div className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-2">Preparation</div><p className="text-sm text-stone-700">{phase.preparation}</p></div>
                      </div>
                      <div>
                        <div className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-3">Exact readiness checklist</div>
                        <div className="space-y-2">{phase.checklist.map((item, index) => {
                          const checked = (progress.checklists?.[phase.n] || []).includes(index);
                          return <label key={item} className={`flex items-start gap-3 ${canEdit ? "cursor-pointer" : "cursor-default"}`}>
                            <input type="checkbox" checked={checked} disabled={!canEdit} onChange={() => toggleChecklist(phase.n, index)} />
                            <span className="text-sm text-stone-700">{item}</span>
                          </label>;
                        })}</div>
                      </div>
                      <div>
                        <div className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-3">Required templates or supporting evidence</div>
                        <div className="flex flex-wrap gap-3">{phase.templates.map((id) => {
                          const template = TEMPLATES.find((t) => t.id === id);
                          return template && <button key={id} disabled={!canEdit} onClick={() => openTemplateModal(template)}
                            className="text-left px-4 py-2 rounded-lg border border-stone-200 bg-white disabled:opacity-60">
                            <div className="text-sm font-semibold text-emerald-900">{template.name}</div>
                            <div className="text-xs text-stone-500">{templateHasRequiredInformation(template, drafts[id]) ? "Required information saved" : "Required information incomplete"}</div>
                          </button>;
                        })}</div>
                        <p className="my-3 text-xs text-stone-500">If the required information lives elsewhere, record exact supporting evidence instead.</p>
                        <textarea disabled={!canEdit} rows={2} value={record.evidenceDetails} onChange={(e) => updateRecord(phase.n, { evidenceDetails: e.target.value })}
                          aria-label={`Phase ${phase.n} supporting evidence`} placeholder="Evidence record: document, result, date, and where it can be reviewed"
                          className="w-full rounded-md border border-stone-200 p-2 text-sm disabled:bg-stone-50" />
                        <input disabled={!canEdit || record.noEvidenceReviewer} value={record.evidenceReviewer} onChange={(e) => updateRecord(phase.n, { evidenceReviewer: e.target.value })}
                          aria-label={`Phase ${phase.n} evidence reviewer`} placeholder="Owner or reviewer"
                          className="mt-2 w-full rounded-md border border-stone-200 p-2 text-sm disabled:bg-stone-50" />
                        <label className="mt-2 flex gap-2 text-xs text-stone-600"><input type="checkbox" disabled={!canEdit} checked={record.noEvidenceReviewer}
                          onChange={(e) => updateRecord(phase.n, { noEvidenceReviewer: e.target.checked, evidenceReviewer: e.target.checked ? "" : record.evidenceReviewer })} />Reviewer not yet assigned — save as self-attested supporting evidence</label>
                        {record.noEvidenceReviewer && <p className="mt-1 text-xs text-amber-700">Self-attested evidence only; reviewer not yet assigned. This is not professional approval.</p>}
                      </div>
                      <div>
                        <div className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-3">Unresolved questions</div>
                        <label className="flex gap-2 text-sm text-stone-700"><input type="checkbox" disabled={!canEdit} checked={record.noUnresolvedQuestions}
                          onChange={(e) => updateRecord(phase.n, { noUnresolvedQuestions: e.target.checked })} />I explicitly declare there are no unresolved questions for this phase.</label>
                        {!record.noUnresolvedQuestions && <>
                          <textarea disabled={!canEdit} rows={2} value={record.questionsDetails} onChange={(e) => updateRecord(phase.n, { questionsDetails: e.target.value })}
                            aria-label={`Phase ${phase.n} unresolved questions`} placeholder="List every unresolved question"
                            className="mt-2 w-full rounded-md border border-stone-200 p-2 text-sm disabled:bg-stone-50" />
                          <input disabled={!canEdit} value={record.questionsOwner} onChange={(e) => updateRecord(phase.n, { questionsOwner: e.target.value })}
                            aria-label={`Phase ${phase.n} question owner`} placeholder="Question owner or reviewer"
                            className="mt-2 w-full rounded-md border border-stone-200 p-2 text-sm disabled:bg-stone-50" />
                          <p className="mt-1 text-xs text-stone-500">Open questions require a named owner or reviewer. Use the declaration above only when there are no unresolved questions.</p>
                        </>}
                      </div>
                      <button onClick={() => openResources(({ 4: "testing", 5: "packaging", 6: "coman", 7: "regulatory", 8: "regulatory" })[phase.n] || "coman")} className="text-sm font-semibold text-emerald-900 underline">View supporting resources for this phase</button>
                      <div className="pt-4 border-t border-stone-100">
                        {isCompleted ? (
                          reopenConfirm === phase.n ? <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                            <p className="text-sm text-amber-950">Reopening phase {phase.n} removes completion from this phase and every later phase. Drafts, checklist entries, evidence, and questions are retained.</p>
                            <div className="mt-3 flex gap-2"><button onClick={() => { setProgress((p) => reopenPhase(p, phase.n)); setReopenConfirm(null); setOpenPhaseN(phase.n); }} className="rounded-full bg-amber-800 px-4 py-2 text-sm font-semibold text-white">Reopen and invalidate downstream</button><button onClick={() => setReopenConfirm(null)} className="text-sm underline">Cancel</button></div>
                          </div> : <button onClick={() => setReopenConfirm(phase.n)} className="rounded-full bg-stone-100 px-4 py-2 text-sm font-semibold text-stone-700">Reopen phase</button>
                        ) : canEdit ? <>
                          <button disabled={!readiness.ready} onClick={() => { setProgress((p) => completePhase(p, phase.n)); setOpenPhaseN(PHASES.find((x) => x.n > phase.n)?.n || phase.n); }}
                            className="rounded-full bg-emerald-800 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Complete phase &amp; continue</button>
                          {!readiness.ready && <p className="mt-2 text-xs text-stone-500">Required: every exact checklist item; saved information in every listed template or a supporting evidence record with reviewer declaration; and unresolved questions with owner/reviewer or an explicit none declaration.</p>}
                        </> : null}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <aside className="hidden lg:block space-y-6 sticky top-24">
        <div className="rounded-xl border border-stone-200 bg-white p-6"><h3 className="font-bold text-stone-900 mb-4">Guided progress</h3>{PHASES.map((phase) => <div key={phase.n} className="flex items-center gap-3 py-1"><span className={`h-2 w-2 rounded-full ${done.includes(phase.n) ? "bg-emerald-500" : "bg-stone-200"}`} /><span className="text-sm text-stone-700">{phase.name}</span></div>)}</div>
        <div className="rounded-xl border border-stone-200 bg-stone-50 p-6"><h3 className="font-bold text-stone-900 mb-2">Work with Alaine &amp; Co</h3><p className="text-sm text-stone-600 mb-4">Get formulation, testing, and co-manufacturer guidance. This does not provide public access to Alaine's Bench.</p><a href="/alaine-co/project-intake" className="inline-flex w-full justify-center px-4 py-2 rounded-full bg-stone-900 text-white text-sm font-semibold">Work with Alaine &amp; Co</a></div>
      </aside>
    </div>
  );
}